import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
// Imported here rather than relied on from CompanyLayout: this modal is shown
// to PROVIDERS too, and provider surfaces never mount CompanyLayout, so the
// .cv-* classes would otherwise be unstyled on half the app.
import '../../styles/company-design-system.css';
// Same slot formatter the negotiation event card and guest negotiation page
// use, so this reads in the interview's own booked timezone (e.g. "Tue, May
// 19 · 9:00 AM ET") rather than silently converting to the viewer's browser
// timezone, which a global app-wide prompt cannot assume matches the slot.
import { formatSlot } from '../../utils/interviewSlotFormat';
import ChoiceGroup from './ChoiceGroup';

/**
 * `consequence` is what this answer DOES, shown only once it is the selected
 * answer. The previous copy stated "your answer confirms the interview took
 * place" above the choices, which is true of exactly one of the four: a
 * respondent about to report a cancellation was reading a promise about a
 * different button.
 *
 * Nothing here claims the outcome is hidden from the counterparty, because it
 * is not: a provider's 'counterparty_no_show' feeds countConfirmedNoShows()
 * in matching/obligation/conduct-recompute.service.ts and counts against the
 * group's conduct record. Only the NOTES are private, and that promise stays
 * attached to the notes field where it is true.
 */
const STEP_ONE = [
  {
    // No static consequence: the true sentence depends on which side is
    // reading it. Resolved from COMPLETION_COPY below.
    value: 'yes',
    label: 'Yes, we spoke',
    consequence: null,
  },
  {
    value: 'not_yet',
    label: 'Not yet, or it was rescheduled',
    consequence:
      'This will not mark the interview complete. We will ask again after the new time.',
  },
  {
    value: 'no',
    label: 'No',
    // True of BOTH sub-reasons, so it is already honest before the sub-choice
    // is made; choosing a reason then replaces it with the more specific line.
    consequence: 'This will not mark the interview complete.',
  },
];

/**
 * The sub-choice behind "No". Each maps onto one of the four unchanged
 * FEEDBACK_OUTCOMES; neither is a peer of "Yes, we spoke".
 */
const NO_REASONS = [
  {
    value: 'counterparty_no_show',
    label: 'The recruiter did not show',
    consequence:
      'This will not mark the interview complete. It becomes part of this interview record.',
  },
  {
    value: 'cancelled',
    label: 'It was cancelled',
    consequence: 'This will not mark the interview complete.',
  },
];

const NO_REASON_QUESTION = 'Which was it?';

/**
 * D1.4.4 STEP 2. Asked only of a PROVIDER, only after 'happened', and only
 * when the listing carries a numeric rate (`compensationIsNumeric`, D1.4.5).
 * The server refuses the field from the company role and refuses it on a
 * comp-blind listing with a 400, so offering the question outside those gates
 * would manufacture an error the respondent did not cause.
 *
 * The consequences say what the answer DOES and stop there. A 'yes' can earn
 * the listing a Compensation Confirmed badge; the other three add nothing to
 * the listing, and none of them is promised a follow-up this phase performs.
 */
const COMPENSATION_QUESTION =
  'Was the compensation consistent with the listing?';

const COMPENSATION_CONSISTENCY = [
  {
    value: 'yes',
    label: 'Yes, it matched',
    consequence:
      'Recorded. A provider confirmation can earn this listing a Compensation Confirmed badge.',
  },
  {
    value: 'partly',
    label: 'Partly',
    consequence: 'Recorded. Nothing is added to the listing.',
  },
  {
    value: 'no',
    label: 'No, it was different',
    consequence: 'Recorded. Nothing is added to the listing.',
  },
  {
    value: 'not_discussed',
    label: 'We did not discuss pay',
    consequence: 'Recorded. Nothing is added to the listing.',
  },
];

/**
 * D1.4.4 STEP 3. Asked of a PROVIDER after 'happened', numeric compensation
 * or not. Named by the recruiter where the backend resolved one, "the
 * recruiter" where it could not (a poster who is not a GROUP user, a legacy
 * row) -- the same fallback the heading uses, for the same reason.
 */
const ENGAGE_AGAIN = [
  { value: 'yes', label: 'Yes, I would' },
  { value: 'maybe', label: 'Maybe' },
  { value: 'no', label: 'No, I would not' },
];

/**
 * Said once under the group rather than four times inside it, because every
 * answer does the same thing. It describes the AGGREGATION (which is what the
 * Provider Recommended evaluator actually reads) and promises nothing about
 * who else can see the row, which is a promise this phase does not enforce.
 */
const ENGAGE_AGAIN_NOTE =
  'This is combined with other providers\' answers. It is never shown on its own.';

/**
 * Step 1's answer, plus the sub-choice behind "No", as one of the four
 * server-side FEEDBACK_OUTCOMES. Null while the answer is incomplete: "No"
 * on its own is a selection, not an answer, and must not be committable.
 *
 * Exported so the mapping can be read and tested without driving the DOM.
 */
export const outcomeFor = (answer, noReason) =>
  answer === 'yes'
    ? 'happened'
    : answer === 'not_yet'
      ? 'rescheduled'
      : answer === 'no'
        ? noReason || null
        : null;

/**
 * The reverse, for claim mode: the counterparty's claim arrives as an
 * OUTCOME and has to be shown back as the step-1 answer it corresponds to.
 * The backend never ships a 'counterparty_no_show' claim (it does not send an
 * accusation to the person it accuses), so only three values reach this.
 */
const answerFor = (outcome) => {
  if (outcome === 'happened') return 'yes';
  if (outcome === 'rescheduled') return 'not_yet';
  return outcome ? 'no' : null;
};

/**
 * The completion rule AS SHIPPED, which is not symmetric: an interview
 * completes when BOTH sides have answered AND the provider's answer is
 * 'happened'. The provider's answer is authoritative on a conflict, in both
 * directions and deliberately. The group is invoiced for a completed
 * interview, so the recruiter's answer is the financially conflicted one and
 * the provider's is not.
 *
 * The copy this replaced stated the OLD "two matching answers" rule, and was
 * false in two OPPOSITE directions on the path that triggers an invoice:
 *
 *  - A recruiter weighing Dispute was told their disagreement blocks
 *    completion. It does not. A provider 'happened' against a company
 *    'cancelled' completes the interview, and invoices the group.
 *  - A provider choosing "It happened" was told it completes only if the
 *    recruiter answers the same way. In fact their answer decides regardless.
 *
 * Keyed by role because the honest sentence differs by who is reading it: a
 * provider told about "the provider's answer" in the third person is being
 * told something about themselves they will not recognise as themselves.
 */
const COMPLETION_COPY = {
  provider: {
    default:
      'Both sides are asked. If your answers disagree, yours is the one that decides.',
    happened:
      'Confirms it took place. Both sides are asked, and yours is the answer that decides.',
  },
  company: {
    default:
      "Both sides are asked. If your answers disagree, the provider's answer decides.",
    happened:
      "Confirms it took place. Both sides are asked, and the provider's answer decides.",
  },
};

// A legacy or cached row may carry no respondentRole at all. The company
// phrasing is true whoever reads it, just a shade more third-person for a
// provider, so it is the safe fallback: neither line becomes false.
function completionCopy(respondentRole) {
  return COMPLETION_COPY[respondentRole] ?? COMPLETION_COPY.company;
}

// The second question, asked only when `item.canProgress` is true (the
// negotiation has not already reached a terminal state) and the attestation
// just chosen warrants it: PROGRESSION after 'happened', REBOOKING after
// 'rescheduled'. 'cancelled' and 'counterparty_no_show' ask nothing here --
// there is no deal state left to advance.
//
// Recruiter and provider get DIFFERENT PROGRESSION lists because they hold
// different facts: a recruiter can start credentialing, a provider cannot,
// and a provider asked what happens next can only honestly say they are
// still waiting to hear back.
//
// 'withdrawn' fires INTERVIEW -> DECLINED, a TERMINAL_STATE with no backward
// edge anywhere in the state machine, so its consequence says the closure
// cannot be undone rather than reusing the softer generic copy the other
// options share.
const PROGRESSION = {
  company: [
    {
      value: 'advancing',
      label: 'Start credentialing',
      consequence: 'Moves this candidate to credentialing.',
    },
    {
      value: 'deciding',
      label: 'Still deciding',
      // No scheduler, nudge or consumer reads this row in this branch, so
      // the old "We will check back" promised an action nothing performs.
      // Say what actually happens: the answer is recorded.
      consequence: 'Nothing moves. Recorded as undecided.',
    },
    {
      value: 'not_advancing',
      label: 'Not moving forward',
      consequence: 'Recorded for your pipeline. Nothing closes yet.',
    },
  ],
  provider: [
    {
      value: 'continue',
      label: 'Yes, still interested',
      consequence: 'Nothing moves. The employer decides the next step.',
    },
    {
      value: 'deciding',
      label: 'Still deciding',
      // No scheduler, nudge or consumer reads this row in this branch, so
      // the old "We will check back" promised an action nothing performs.
      // Say what actually happens: the answer is recorded.
      consequence: 'Nothing moves. Recorded as undecided.',
    },
    {
      value: 'withdrawn',
      label: 'No longer interested',
      consequence: 'This closes the negotiation. It cannot be reopened.',
    },
  ],
};

const REBOOKING = [
  {
    value: 'rebooked',
    label: 'Yes, the new time is booked',
    consequence: 'Nothing else needed.',
  },
  {
    value: 'not_rebooked',
    label: 'Not yet',
    // Nothing follows up on this today. The row is written and that is all,
    // so the copy says exactly that rather than promising a nudge.
    consequence: 'Recorded. No new time is booked yet.',
  },
];

// Which list (if any) the second question offers.
//
// REBOOKING IS CHECKED FIRST, AND IS NOT GATED ON canProgress. "Is the new
// time already booked?" fires no transition and needs no state legality: it
// records a fact about the calendar, not a move in the negotiation. Returning
// null before the two questions were even distinguished meant an interview
// whose negotiation had left 'interview' was never asked whether a new time
// had been booked, which is the exact dead-end the rebooking question was
// added to close.
//
// PROGRESSION stays gated. Those options DO move the deal, and offering one
// on a negotiation that cannot take it produces an error the respondent did
// not cause rather than a real choice.
function progressionOptionsFor({ canProgress, respondentRole }, outcome) {
  if (outcome === 'rescheduled') return REBOOKING;
  if (!canProgress) return null;
  if (outcome === 'happened') return PROGRESSION[respondentRole] ?? null;
  return null;
}

// The question text itself. Only ever read while progressionOptionsFor(...)
// is non-null, so the two outcomes it distinguishes are the only two that
// matter here.
function progressionQuestionFor({ respondentRole }, outcome) {
  if (outcome === 'rescheduled') return 'Is the new time already booked?';
  return respondentRole === 'company'
    ? 'Are you moving forward?'
    : 'Are you still interested?';
}

// Stated as a claim, never as a checked default -- the counterparty's answer
// only becomes THIS respondent's answer once Confirm is pressed. There is no
// entry here for 'counterparty_no_show': the backend omits that claim value
// entirely rather than shipping an accusation to the person it accuses, so
// this component never receives it and must not grow a branch for it.
const CLAIM_SENTENCE = {
  happened: 'reported that this interview happened.',
  cancelled: 'reported that this interview was cancelled.',
  rescheduled: 'reported that this interview was rescheduled.',
};

// Everything the browser would put in the tab order. Filtered by tabIndex >= 0
// at trap time so the roving-tabindex radios (all but one sit at -1) are not
// treated as stops.
const FOCUSABLE = 'button, textarea, input, select, a[href]';

const TEAL = '#14B8A6';
const TEAL_DEEP = '#0F766E';
const TEAL_WASH = '#F0FDFA';
const GRAY_LINE = '#E2E8F0';
const GRAY_DOT = '#CBD5E1';

// The notice is amber, not red, and that is the whole point of it being a
// separate prop from `error`. Red says "this did not save, try again"; the
// answer DID save. Amber says "saved, but the thing we promised alongside it
// could not happen" -- a different fact, needing a different colour and a
// different control (Close, never Submit). Values are the design system's own
// --cv-amber-* tokens; inline because the system defines no notice/callout
// class and inline styling is this component's established pattern for
// anything it lacks.
const AMBER_WASH = '#FFFBEB';
const AMBER_LINE = '#F59E0B';
const AMBER_DEEP = '#B45309';

// Confirm and Dispute share this one object so they cannot drift apart in
// weight. Equal visual weight is the whole safety property of this step: the
// moment Confirm reads as a primary and Dispute as a grey link, this becomes
// a rubber stamp with extra steps.
//
// background/border/color are deliberately NOT set here, even though
// `.cv-btn cv-btn--secondary` is also on both buttons: an inline style always
// outranks `.cv-btn--secondary:hover`, so duplicating those three properties
// here would silently kill the hover state the class already supplies (which
// happens to resolve to the exact same values: cv-white, 1px cv-gray-200,
// cv-gray-700). Leave them to the class and hover works again.
const peerButton = {
  flex: 1,
  padding: '11px 14px',
  borderRadius: 8,
  fontFamily: 'inherit',
  fontSize: 14,
  fontWeight: 600,
  cursor: 'pointer',
};

/**
 * Asked of BOTH sides after a scheduled interview time passes. An interview
 * completes when both sides have answered AND the PROVIDER's answer is
 * 'happened', which is the evidence the Premium gate reads and what admits
 * the row to invoicing.
 *
 * That rule is asymmetric on purpose, so the copy has to be too. Both sides
 * are still asked, and neither side alone can complete an interview: a
 * recruiter who believed otherwise would think they can self-certify. But a
 * DISAGREEMENT is settled by the provider, in both directions. The group is
 * invoiced for a completed interview, so the recruiter's answer is the
 * financially conflicted one. See COMPLETION_COPY above, which is where that
 * asymmetry is said out loud to each reader.
 *
 * CHOOSING IS NOT COMMITTING, AND THAT IS THE POINT. The write is immutable
 * (interview-feedback.service.ts refuses to revise an answer once filed), and
 * this modal appears unbidden over whatever page the user was on, so a single
 * stray click must not be able to file an irreversible answer -- one of the
 * four is an accusation against a named person. Selection is local state;
 * only Submit calls onSubmit.
 *
 * That ordering also un-strands the notes field. It used to sit BELOW four
 * buttons that each submitted on click, so a note typed after answering
 * arrived too late to be sent, and the only users who ever got a note through
 * were the ones who happened to type before choosing.
 *
 * PORTALLED TO <body>, WHICH IS NOT STYLISTIC. `.cv-modal` is `position: fixed`,
 * and a fixed element resolves against the nearest ancestor carrying a
 * transform, filter or backdrop-filter rather than the viewport. Rendered
 * inside a card it lands off-centre. See FeedActionConfirmModal.jsx.
 *
 * A second question -- PROGRESSION, or REBOOKING after a reschedule -- follows
 * the attestation when `item.canProgress` is true. The two sides are asked
 * different things because they hold different facts: a recruiter can start
 * credentialing, a provider cannot, and a provider asked what happens next
 * can only honestly say they are waiting to hear back. Choosing a progression
 * option is still just a selection -- the same Submit commits both answers
 * together, nothing about the two-step shape changes.
 *
 * @param {object|null} item  the pending interview, or null to render nothing.
 *   `slot` and `counterpartyName` are best-effort context (who/when this
 *   interview was): both are null on a row the backend could not resolve
 *   them for (legacy/backfilled rows), so this must still read sensibly with
 *   neither present. `counterpartyClaim` ('happened' | 'cancelled' |
 *   'rescheduled' | null/absent) opens the modal in claim mode; the backend
 *   omits it entirely for a 'counterparty_no_show' claim, so that value never
 *   reaches this component. `canProgress` (boolean) gates the progression
 *   question: false once the negotiation has already reached a terminal
 *   state, in which case nothing here asks about next steps.
 *   `respondentRole` ('provider' | 'company') selects which progression
 *   question and options apply.
 * @param {boolean} [busy]    a submission is in flight
 * @param {string|null} [error] a failed submission, surfaced in place
 * @param {string|null} [notice] a submission that SUCCEEDED but whose
 *   progression half did not take effect: the answer is filed, and the thing
 *   the consequence line promised ("Moves this candidate to credentialing",
 *   "This closes the negotiation") did not happen because the negotiation had
 *   already moved on. Setting it puts the modal in NOTICE MODE, which renders
 *   the notice and a single Close button and nothing else -- see below. This
 *   is deliberately NOT `error`: the answer saved, so red-and-retry would be
 *   its own lie.
 * @param {function} onSubmit ({ scheduledInterviewId, outcome, notes, disposition? })
 *   `disposition` is included only when a progression option was chosen.
 * @param {function} onClose  fires on "Not now", Escape, backdrop click, and
 *   on notice mode's Close
 */
export default function InterviewFeedbackModal({
  item,
  busy = false,
  error = null,
  notice = null,
  onSubmit,
  onClose,
}) {
  // Step 1's answer ('yes' | 'not_yet' | 'no') and, behind "No", which kind
  // of no it was. The committed outcome is DERIVED from the pair by
  // outcomeFor: nothing is committable until the pair resolves to one of the
  // four FEEDBACK_OUTCOMES.
  const [answer, setAnswer] = useState(null);
  const [noReason, setNoReason] = useState(null);
  const [notes, setNotes] = useState('');
  // D1.4.4 steps 2 and 3. Cleared alongside `disposition` whenever step 1
  // changes: a compensation answer given behind a previous 'happened' must
  // not survive a change of mind to 'cancelled', where the server refuses it.
  const [compensationConsistency, setCompensationConsistency] = useState(null);
  const [wouldEngageAgain, setWouldEngageAgain] = useState(null);
  // The answer to the second question (PROGRESSION or REBOOKING), reset
  // whenever `outcome` changes -- 'advancing' only makes sense after
  // 'happened', REBOOKING's values only make sense after 'rescheduled', and
  // 'cancelled'/'counterparty_no_show' ask nothing here at all. See
  // `chooseOutcome` below, the single place `outcome` is ever set.
  const [disposition, setDisposition] = useState(null);
  // 'claim' when the other side already answered and this respondent gets
  // Confirm/Dispute instead of the open question. Set once, from the item
  // this instance was mounted for -- InterviewFeedbackPrompt keys the modal
  // on scheduledInterviewId, so a new interview is always a fresh mount, the
  // same reason `outcome` and `notes` above don't need to be resynced either.
  const [mode, setMode] = useState(item?.counterpartyClaim ? 'claim' : 'open');
  const modalRef = useRef(null);
  const radioRefs = useRef([]);
  const progressionRadioRefs = useRef([]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    // Land on the dialog itself, not on a control. Announces the title, and
    // leaves no control pre-armed for a stray Enter.
    //
    // Re-run on a change of `notice`, not only on mount. Entering notice mode
    // unmounts every control the user could have been focused on (Submit was
    // the last thing they clicked), which drops focus to <body> -- outside the
    // dialog, where trapTab's keydown handler never fires and Tab walks
    // straight into the page behind. Same reasoning in reverse on the way out.
    const t = setTimeout(() => modalRef.current?.focus(), 0);
    return () => clearTimeout(t);
  }, [notice]);

  // An app-wide dialog whose Tab key walks out into the page behind it is a
  // dialog in appearance only.
  const trapTab = useCallback((e) => {
    if (e.key !== 'Tab' || !modalRef.current) return;
    const stops = Array.from(
      modalRef.current.querySelectorAll(FOCUSABLE),
    ).filter((el) => !el.disabled && el.tabIndex >= 0);
    if (!stops.length) return;

    const first = stops[0];
    const last = stops[stops.length - 1];
    const active = document.activeElement;

    if (e.shiftKey && (active === first || active === modalRef.current)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  }, []);

  // The only place step 1's answer is ever set. Always clears BOTH the
  // sub-choice and `disposition` alongside it, so neither a reason chosen
  // behind a previous "No" nor a progression answer chosen for a previous
  // outcome can survive a change of mind to one it no longer applies to.
  const chooseAnswer = (value) => {
    setAnswer(value);
    setNoReason(null);
    setDisposition(null);
    setCompensationConsistency(null);
    setWouldEngageAgain(null);
  };

  const chooseNoReason = (value) => {
    setNoReason(value);
    setDisposition(null);
    setCompensationConsistency(null);
    setWouldEngageAgain(null);
  };

  // Arrow keys move the selection, which is what a radiogroup owes a keyboard
  // user. Harmless here precisely because moving the selection commits nothing.
  const onGroupKeyDown = (e) => {
    const forward = e.key === 'ArrowDown' || e.key === 'ArrowRight';
    const back = e.key === 'ArrowUp' || e.key === 'ArrowLeft';
    if (!forward && !back) return;
    e.preventDefault();

    const dir = forward ? 1 : -1;
    const current = STEP_ONE.findIndex((o) => o.value === answer);
    const from = current === -1 ? (forward ? -1 : 0) : current;
    const next = (from + dir + STEP_ONE.length) % STEP_ONE.length;

    chooseAnswer(STEP_ONE[next].value);
    radioRefs.current[next]?.focus();
  };

  // NOTICE MODE. The question has already been answered -- that is the
  // precondition for a notice existing at all -- so re-showing it would invite
  // a second answer to a question that is closed, and the attestation write is
  // immutable. Renders the notice and a single Close: no question, no radios,
  // no notes, no Submit.
  //
  // Checked BEFORE the `!item` guard on purpose. A notice says a promise the
  // product made was not kept, and swallowing it because the pending list had
  // already moved on would be the same silence this whole change exists to
  // end. The prompt does retain the item; this does not depend on it.
  if (notice) {
    return createPortal(
      <div
        className="cv-modal-overlay"
        role="dialog"
        aria-modal="true"
        aria-labelledby="if-notice"
        onClick={(e) => {
          if (e.target === e.currentTarget) onClose?.();
        }}
      >
        <div
          className="cv-modal"
          ref={modalRef}
          tabIndex={-1}
          onKeyDown={trapTab}
          style={{ outline: 'none' }}
        >
          {/* role="status", not role="alert". Nothing failed for the user to
              fix: their answer is filed. An alert would put this in the same
              register as "We could not save that answer", which is the
              confusion the separate prop exists to avoid. */}
          <p
            id="if-notice"
            role="status"
            className="cv-text-sm"
            style={{
              background: AMBER_WASH,
              color: AMBER_DEEP,
              border: `1px solid ${AMBER_LINE}`,
              borderRadius: 8,
              padding: '12px 14px',
              lineHeight: 1.5,
              margin: '0 0 16px',
            }}
          >
            {notice}
          </p>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button
              type="button"
              className="cv-btn cv-btn--primary"
              onClick={onClose}
            >
              Close
            </button>
          </div>
        </div>
      </div>,
      document.body,
    );
  }

  if (!item) return null;

  const answered = STEP_ONE.find((o) => o.value === answer) ?? null;
  const chosenReason = NO_REASONS.find((r) => r.value === noReason) ?? null;
  const outcome = outcomeFor(answer, noReason);

  // The client mirrors the server's refusals exactly (D1.4.4): step 2 needs
  // 'happened' AND the provider role AND a numeric listing rate; step 3 needs
  // 'happened' AND the provider role. A question the server would reject is a
  // question this modal must not ask.
  const isProvider = item.respondentRole === 'provider';
  const showStep2 =
    outcome === 'happened' && isProvider && item.compensationIsNumeric === true;
  const showStep3 = outcome === 'happened' && isProvider;

  const canCommit =
    Boolean(outcome) &&
    !busy &&
    (!showStep2 || Boolean(compensationConsistency)) &&
    (!showStep3 || Boolean(wouldEngageAgain));

  // 'happened' carries no static consequence: what it DOES depends on which
  // side is answering, because the provider's answer is the one that settles
  // a disagreement. The other three read the same to everyone.
  const copy = completionCopy(item.respondentRole);
  const consequence = answered
    ? (chosenReason?.consequence ?? answered.consequence ?? copy.happened)
    : copy.default;

  const progressionOptions = progressionOptionsFor(item, outcome);
  const progressionQuestion = progressionOptions
    ? progressionQuestionFor(item, outcome)
    : null;
  const selectedProgression =
    progressionOptions?.find((o) => o.value === disposition) ?? null;

  const onProgressionKeyDown = (e) => {
    const forward = e.key === 'ArrowDown' || e.key === 'ArrowRight';
    const back = e.key === 'ArrowUp' || e.key === 'ArrowLeft';
    if (!forward && !back) return;
    e.preventDefault();

    const dir = forward ? 1 : -1;
    const current = progressionOptions.findIndex((o) => o.value === disposition);
    const from = current === -1 ? (forward ? -1 : 0) : current;
    const next =
      (from + dir + progressionOptions.length) % progressionOptions.length;

    setDisposition(progressionOptions[next].value);
    progressionRadioRefs.current[next]?.focus();
  };

  const commit = () => {
    if (!canCommit) return;
    onSubmit?.({
      scheduledInterviewId: item.scheduledInterviewId,
      outcome,
      notes,
      // Steps 2 and 3 ride the SAME post as step 1 (D1.4.4: one write, the
      // attestation row is $setOnInsert and a later post is a no-op), and are
      // omitted entirely when their gate did not open.
      ...(showStep2 && compensationConsistency ? { compensationConsistency } : {}),
      ...(showStep3 && wouldEngageAgain ? { wouldEngageAgain } : {}),
      // Omitted entirely (not sent as null/undefined) when no progression
      // option was chosen, so a payload with nothing to progress stays
      // exactly { scheduledInterviewId, outcome, notes } -- unchanged shape
      // for every caller that predates this question.
      ...(disposition ? { disposition } : {}),
    });
  };

  // Best-effort "who and when": a respondent with more than one pending
  // interview cannot otherwise tell which one this prompt means. Either half
  // can be missing (row the backend could not resolve a name for; a slot
  // with no startTime/timezone), so this degrades to whichever half it has
  // rather than requiring both.
  const formattedSlot = item.slot ? formatSlot(item.slot) : '';
  const hasContext = Boolean(item.counterpartyName || formattedSlot);

  // D1.4.6: the provider is told WHO they are being asked about, by first
  // name and last initial, and "the recruiter" when the backend could not
  // resolve one (a poster who is not a GROUP user, a legacy row). A recruiter
  // is asked the same question the other way round, because "the recruiter"
  // would be themselves; their counterparty is already named in the context
  // line above, so the heading does not repeat it.
  const aboutListing = item.listingTitle ? ` about ${item.listingTitle}` : '';
  const askedAbout =
    item.respondentRole === 'company'
      ? 'the provider'
      : item.recruiterDisplayName || 'the recruiter';
  const question = `Did you speak with ${askedAbout}${aboutListing}?`;

  return createPortal(
    <div
      className="cv-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="if-title"
      aria-describedby={hasContext ? 'if-context if-body' : 'if-body'}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div
        className="cv-modal"
        ref={modalRef}
        tabIndex={-1}
        aria-busy={busy || undefined}
        onKeyDown={trapTab}
        style={{ outline: 'none' }}
      >
        <h2
          id="if-title"
          style={{
            fontSize: 18,
            fontWeight: 700,
            margin: 0,
            color: '#1e293b',
            letterSpacing: '-0.01em',
          }}
        >
          {question}
        </h2>

        {hasContext && (
          <p
            id="if-context"
            className="cv-text-sm"
            style={{ color: '#64748b', margin: '4px 0 0' }}
          >
            {item.counterpartyName
              ? `Interview with ${item.counterpartyName}`
              : 'Interview'}
            {formattedSlot ? ` · ${formattedSlot}` : ''}
          </p>
        )}

        {/* The claim as a sentence, never a checked default: Confirm and
            Dispute are equal peers below it, sharing `peerButton` so neither
            can end up looking like the "real" answer. Confirm carries the
            claim forward as the selection; Dispute clears it. Either way
            control passes to the untouched radiogroup below, in `open` mode. */}
        {mode === 'claim' && (
          <>
            <p
              className="cv-text-sm"
              style={{ color: '#334155', margin: '16px 0 4px' }}
            >
              {item.counterpartyName ?? 'The other side'}{' '}
              {CLAIM_SENTENCE[item.counterpartyClaim]}
            </p>
            <p
              className="cv-text-sm"
              style={{ color: '#64748b', margin: '0 0 12px' }}
            >
              Confirm if that matches your record.
            </p>
            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
              <button
                type="button"
                className="cv-btn cv-btn--secondary"
                style={peerButton}
                onClick={() => {
                  // The claim arrives as an OUTCOME and is shown back as the
                  // step-1 answer it corresponds to, sub-choice included.
                  chooseAnswer(answerFor(item.counterpartyClaim));
                  if (item.counterpartyClaim === 'cancelled') {
                    setNoReason('cancelled');
                  }
                  setMode('open');
                }}
              >
                Confirm
              </button>
              <button
                type="button"
                className="cv-btn cv-btn--secondary"
                style={peerButton}
                onClick={() => {
                  chooseAnswer(null);
                  setMode('open');
                }}
              >
                Dispute
              </button>
            </div>
          </>
        )}

        {mode === 'open' && (
          <div
            role="radiogroup"
            aria-labelledby="if-title"
            onKeyDown={onGroupKeyDown}
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 6,
              margin: '16px 0 0',
            }}
          >
            {STEP_ONE.map((option, idx) => {
              const isOn = answer === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  role="radio"
                  aria-checked={isOn}
                  // Roving tabindex: the group is ONE tab stop, and arrow keys
                  // move within it. Three separate stops is what a list of plain
                  // buttons gives you, and it reads as three unrelated toggles.
                  tabIndex={isOn || (!answer && idx === 0) ? 0 : -1}
                  ref={(el) => {
                    radioRefs.current[idx] = el;
                  }}
                  disabled={busy}
                  onClick={() => chooseAnswer(option.value)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    width: '100%',
                    textAlign: 'left',
                    padding: '11px 14px',
                    borderRadius: 8,
                    // Selection is carried by border, wash AND the filled dot,
                    // never by colour alone.
                    border: `1px solid ${isOn ? TEAL : GRAY_LINE}`,
                    boxShadow: isOn ? `0 0 0 1px ${TEAL}` : 'none',
                    background: isOn ? TEAL_WASH : '#fff',
                    color: isOn ? TEAL_DEEP : '#334155',
                    fontFamily: 'inherit',
                    fontSize: 14,
                    fontWeight: isOn ? 600 : 500,
                    lineHeight: 1.4,
                    cursor: busy ? 'not-allowed' : 'pointer',
                    opacity: busy ? 0.6 : 1,
                    transition:
                      'border-color 120ms ease, background 120ms ease',
                  }}
                >
                  <span
                    aria-hidden="true"
                    style={{
                      flex: '0 0 auto',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 16,
                      height: 16,
                      borderRadius: '50%',
                      border: `2px solid ${isOn ? TEAL : GRAY_DOT}`,
                      background: '#fff',
                    }}
                  >
                    {isOn && (
                      <span
                        style={{
                          width: 7,
                          height: 7,
                          borderRadius: '50%',
                          background: TEAL,
                        }}
                      />
                    )}
                  </span>
                  {option.label}
                </button>
              );
            })}
          </div>
        )}

        {/* The sub-choice behind "No", asked only once "No" is the answer.
            Two shapes of the same answer are not peers of "Yes, we spoke":
            asking them as peers made a respondent pick a REASON before they
            had given an ANSWER. The options carry no per-option consequence
            here because the one below states it for the pair. */}
        {mode === 'open' && answer === 'no' && (
          <div style={{ margin: '10px 0 0' }}>
            <ChoiceGroup
              name="if-no-reason"
              question={NO_REASON_QUESTION}
              options={NO_REASONS.map(({ value, label }) => ({ value, label }))}
              value={noReason}
              onChange={chooseNoReason}
              disabled={busy}
            />
          </div>
        )}

        <p
          id="if-body"
          className="cv-text-sm"
          style={{
            color: answered ? TEAL_DEEP : '#64748b',
            lineHeight: 1.5,
            margin: '10px 0 16px',
          }}
        >
          {consequence}
        </p>

        {/* D1.4.4 STEP 2. Provider only, 'happened' only, numeric listing
            rate only. Rendered through ChoiceGroup so the third and fourth
            questions in this dialog behave identically to the first two for a
            keyboard user. */}
        {showStep2 && (
          <div style={{ margin: '0 0 16px' }}>
            <ChoiceGroup
              name="if-compensation"
              question={COMPENSATION_QUESTION}
              options={COMPENSATION_CONSISTENCY}
              value={compensationConsistency}
              onChange={setCompensationConsistency}
              disabled={busy}
            />
          </div>
        )}

        {/* D1.4.4 STEP 3. Provider only, 'happened' only. */}
        {showStep3 && (
          <div style={{ margin: '0 0 16px' }}>
            <ChoiceGroup
              name="if-engage-again"
              question={`Would you be comfortable engaging with ${askedAbout} again?`}
              options={ENGAGE_AGAIN}
              value={wouldEngageAgain}
              onChange={setWouldEngageAgain}
              disabled={busy}
            />
            <p
              className="cv-text-sm"
              style={{ color: '#64748b', lineHeight: 1.5, margin: '6px 0 0' }}
            >
              {ENGAGE_AGAIN_NOTE}
            </p>
          </div>
        )}

        {/* The second question. Same shape as the radiogroup above (roving
            tabindex, arrow-key nav, border+wash+dot selection) so the two
            questions in one modal behave identically to a keyboard user.
            Deliberately duplicated rather than pulled into a shared
            radiogroup component -- that extraction was considered and
            deferred. */}
        {progressionOptions && (
          <div style={{ margin: '0 0 4px' }}>
            <p
              id="if-progression-title"
              className="cv-text-sm"
              style={{ color: '#1e293b', fontWeight: 600, margin: '0 0 6px' }}
            >
              {progressionQuestion}
            </p>
            <div
              role="radiogroup"
              aria-labelledby="if-progression-title"
              onKeyDown={onProgressionKeyDown}
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 6,
                margin: '0 0 6px',
              }}
            >
              {progressionOptions.map((option, idx) => {
                const isOn = disposition === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    role="radio"
                    aria-checked={isOn}
                    tabIndex={isOn || (!disposition && idx === 0) ? 0 : -1}
                    ref={(el) => {
                      progressionRadioRefs.current[idx] = el;
                    }}
                    disabled={busy}
                    onClick={() => setDisposition(option.value)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      width: '100%',
                      textAlign: 'left',
                      padding: '11px 14px',
                      borderRadius: 8,
                      border: `1px solid ${isOn ? TEAL : GRAY_LINE}`,
                      boxShadow: isOn ? `0 0 0 1px ${TEAL}` : 'none',
                      background: isOn ? TEAL_WASH : '#fff',
                      color: isOn ? TEAL_DEEP : '#334155',
                      fontFamily: 'inherit',
                      fontSize: 14,
                      fontWeight: isOn ? 600 : 500,
                      lineHeight: 1.4,
                      cursor: busy ? 'not-allowed' : 'pointer',
                      opacity: busy ? 0.6 : 1,
                      transition:
                        'border-color 120ms ease, background 120ms ease',
                    }}
                  >
                    <span
                      aria-hidden="true"
                      style={{
                        flex: '0 0 auto',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 16,
                        height: 16,
                        borderRadius: '50%',
                        border: `2px solid ${isOn ? TEAL : GRAY_DOT}`,
                        background: '#fff',
                      }}
                    >
                      {isOn && (
                        <span
                          style={{
                            width: 7,
                            height: 7,
                            borderRadius: '50%',
                            background: TEAL,
                          }}
                        />
                      )}
                    </span>
                    {option.label}
                  </button>
                );
              })}
            </div>
            {selectedProgression && (
              <p
                className="cv-text-sm"
                style={{
                  color: TEAL_DEEP,
                  lineHeight: 1.5,
                  margin: '0 0 16px',
                }}
              >
                {selectedProgression.consequence}
              </p>
            )}
          </div>
        )}

        <label
          htmlFor="if-notes"
          className="cv-text-sm"
          style={{ display: 'block', color: '#475569', marginBottom: 6 }}
        >
          Anything we should know? Optional.
        </label>
        <textarea
          id="if-notes"
          // height: auto overrides .cv-input's fixed 40px, which is sized for a
          // single-line input and would crush rows={3} to one line.
          className="cv-input"
          value={notes}
          maxLength={2000}
          rows={3}
          disabled={busy}
          onChange={(e) => setNotes(e.target.value)}
          style={{
            width: '100%',
            marginBottom: 6,
            height: 'auto',
            resize: 'vertical',
          }}
        />
        <p
          className="cv-text-sm"
          style={{ color: '#64748b', lineHeight: 1.5, margin: '0 0 16px' }}
        >
          Only Rx Rooster sees this. It is never shown to the other side.
        </p>

        {error && (
          <div
            role="alert"
            className="cv-text-sm"
            style={{
              background: '#FEF2F2',
              color: '#B91C1C',
              border: '1px solid #FECACA',
              borderRadius: 8,
              padding: '10px 12px',
              lineHeight: 1.5,
              margin: '0 0 14px',
            }}
          >
            {error}
          </div>
        )}

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
          }}
        >
          <button
            type="button"
            className="cv-btn cv-btn--ghost"
            disabled={busy}
            onClick={onClose}
          >
            Not now
          </button>
          <button
            type="button"
            className="cv-btn cv-btn--primary"
            disabled={!canCommit}
            onClick={commit}
            // Not a dimmed primary: white-on-faded-teal reads as a smudge
            // rather than as unavailable. Grey states it plainly.
            style={
              canCommit
                ? undefined
                : {
                    background: GRAY_LINE,
                    color: '#94A3B8',
                    cursor: 'not-allowed',
                  }
            }
          >
            {busy ? 'Saving...' : 'Submit'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
